import * as React from 'react'

import Loader from 'react-loader-spinner'

import styled from 'theme/styled-components'
import useTheme from 'theme/useTheme'

import Icons from 'components/icons/Icons'
import Modal from 'components/modal/Modal'

import LockerDirectory from 'directory/locker/LockerDirectory'

import useI18n from 'i18n/useI18n'

import api from './api'
import OpenButton from './OpenButton'

import ToastAlert from 'components/toast/ToastAlert'
import ValidationContent from 'components/modal/ValidationContent'
import Logger from 'utils/Logger'

interface Props {
  refreshLockers: () => void
  locker: LockerDetail
  userType?: UserType
  siteId?: string
  isShareLoading: boolean
  setIsShareLoading: (isLoading: boolean) => void
}

const LockerItem = ({ refreshLockers, locker, userType, siteId, isShareLoading, setIsShareLoading }: Props) => {
  const i18n = useI18n()
  const [Theme] = useTheme()

  const name = locker.sharedUsers
    ? `${locker.sharedUsers.firstName} ${locker.sharedUsers.lastName}`
    : locker.owner
    ? `${locker.owner.firstName} ${locker.owner.lastName}`
    : ''

  const onContactSelect = (contact: LockerUser) => {
    if (userType && siteId && contact.id) {
      setIsShareLoading(true)
      Modal.close()
      api
        .shareLocker(userType, siteId, locker.id, contact.id)
        .then(() => refreshLockers())
        .catch((error) => {
          Logger.error(error)
          ToastAlert.open(() => (
            <ValidationContent title={i18n.t('screens.locker.error')} onConfirm={ToastAlert.close} />
          ))
        })
    }
  }

  const onShareButtonClick = () => {
    if (siteId && userType && locker.sharedUsers) {
      setIsShareLoading(true)
      api
        .unshareLocker(userType, siteId, locker.id, locker.sharedUsers.id)
        .then(() => refreshLockers())
        .catch((error) => {
          Logger.error(error)
          ToastAlert.open(() => (
            <ValidationContent title={i18n.t('screens.locker.error')} onConfirm={ToastAlert.close} />
          ))
        })
    } else {
      Modal.open(() => (
        <LockerDirectory
          lockerId={locker.id}
          onSelectContact={onContactSelect}
          searchAriaLabel={i18n.t('label.ariaLabel.guest.searchVisitorInDirectory')}
        />
      ))
    }
  }

  const isOpenable = locker.status === 'AVAILABLE'
  const isShareable = isOpenable && locker.isShareable

  return (
    <Container>
      <IconContainer>
        <Icons name="lockers-shelf" size={62} />
      </IconContainer>
      <RightContainer>
        <DetailsContainer>
          <LockerNumber>{i18n.t('screens.locker.lockerNumber', { number: locker.fullDoorNumber })}</LockerNumber>
          <ShareUserContainer>
            {locker.sharedUsers ? (
              <ShareUser>{i18n.t('screens.locker.sharedWith')}</ShareUser>
            ) : locker.owner ? (
              <ShareUser>{i18n.t('screens.locker.sharedBy')}</ShareUser>
            ) : (
              <ShareUser />
            )}
            <ShareUser bold>{name}</ShareUser>
          </ShareUserContainer>
        </DetailsContainer>
        <ButtonsContainer>
          <OpenButton
            lockerId={locker.id}
            userType={userType}
            siteId={siteId}
            duration={locker.openTime * 1000}
            disabled={!isOpenable}
          />
          {!locker.owner && (
            <ShareButton onClick={onShareButtonClick} disabled={!isShareable}>
              <ButtonLabel disabled={!isShareable}>
                {isShareLoading ? (
                  <Loader type="ThreeDots" color={Theme.colors.white} height={20} width={40} />
                ) : (
                  i18n.t(`screens.locker.${locker.sharedUsers ? 'stopSharing' : 'share'}`)
                )}
              </ButtonLabel>
            </ShareButton>
          )}
        </ButtonsContainer>
      </RightContainer>
    </Container>
  )
}

export default LockerItem

const Container = styled.div`
  display: flex;
  align-items: left;
  justify-content: left;
  flex-direction: row;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border: 2px solid ${(props) => props.theme.colors.mediumLightGrey};
  height: 250px;
  max-width: 600px;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 200px;
  background-color: ${(props) => props.theme.colors.mediumLightGrey};
`

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 14px 18px;
`

const DetailsContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  color: ${(props) => props.theme.colors.darkGrey};
`

const LockerNumber = styled.p`
  ${(props) => props.theme.fonts.h2Bold};
  margin: 0;
`

const ShareUserContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

const ShareUser = styled.p<{ bold?: boolean }>`
  ${(props) => (props.bold ? props.theme.fonts.subtitleBold : props.theme.fonts.subtitle)};
  margin: 10px 0;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ShareButton = styled.button`
  display: flex;
  width: 100%;
  height: 50px;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  background-color: ${(props) => props.theme.colors.blue};
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  transition: background-color 0.3s ease;

  &:disabled {
    cursor: not-allowed;
    background-color: ${(props) => props.theme.colors.mediumDarkGrey};
  }
`

const ButtonLabel = styled.p<{ disabled?: boolean }>`
  ${(props) => (props.disabled ? props.theme.fonts.body : props.theme.fonts.bodyBold)};
  color: ${(props) => (props.disabled ? props.theme.colors.placeholderGrey : props.theme.colors.white)};
`
